import cookie from "cookie"
import type { IncomingMessage, ServerResponse } from "http"

export const isBrowser = typeof window !== "undefined"

export function parseCookies(
  req?: IncomingMessage,
  options = {},
): Record<string, string> {
  return cookie.parse(req ? req.headers.cookie || "" : document.cookie, options)
}

export function removeCookie(
  name: string,
  res: ServerResponse,
  options?: {
    domain?: string
    path?: string
    secure?: boolean
    sameSite?: "strict" | "lax" | "none" | boolean
  },
): void {
  const parts = [`${name}=deleted`, "Max-Age=0"]

  if (options?.path) parts.push(`Path=${options.path}`)
  if (options?.domain) parts.push(`Domain=${options.domain}`)
  if (options?.secure) parts.push("Secure")
  if (options?.sameSite) parts.push(`SameSite=${options.sameSite}`)

  res.setHeader("Set-Cookie", parts.join("; "))
}

export function unique<T, K extends keyof T>(items: T[], key: K) {
  const result: T[] = []
  const map = new Map()
  items.forEach((item) => {
    if (!map.has(item[key])) {
      map.set(item[key], true)
      result.push(item)
    }
  })
  return result
}

/**
 * Return a new object with some properties omitted.
 */
export function objectOmit(obj: { [k: string]: any }, keys: string[]) {
  return Object.fromEntries(
    Object.entries(obj).filter(([key]) => !keys.includes(key)),
  )
}

/**
 * Return a new object with some properties included.
 */
export function objectPick(obj: { [k: string]: any }, keys: string[]) {
  return Object.fromEntries(
    Object.entries(obj).filter(([key]) => keys.includes(key)),
  )
}

/**
 * Filter object entries like Array.filter.
 */
export function objectFilter(
  obj: Record<string, any>,
  filter: ([key, value]: [key: string, value: any]) => boolean,
): Record<string, any> {
  const newObj: Record<string, any> = {}

  if (!obj) {
    return newObj
  }

  Object.keys(obj).forEach((key) => {
    if (filter([key, obj[key]])) {
      newObj[key] = obj[key]
    }
  })

  return newObj
}

/**
 * Creates a hash from a string.
 *
 * @see https://stackoverflow.com/a/52171480/2270233
 * @see https://github.com/bryc/code/blob/master/jshash/experimental/cyrb53.js
 *
 * @param str The string to hash
 * @param seed Supply an unsigned integer, 32-bit max for alternate streams of the same input
 */
export const hash = function (str: string, seed = 0): string {
  let h1 = 0xdeadbeef ^ seed,
    h2 = 0x41c6ce57 ^ seed
  for (let i = 0, ch; i < str.length; i++) {
    ch = str.charCodeAt(i)
    h1 = Math.imul(h1 ^ ch, 2654435761)
    h2 = Math.imul(h2 ^ ch, 1597334677)
  }
  h1 =
    Math.imul(h1 ^ (h1 >>> 16), 2246822507) ^
    Math.imul(h2 ^ (h2 >>> 13), 3266489909)
  h2 =
    Math.imul(h2 ^ (h2 >>> 16), 2246822507) ^
    Math.imul(h1 ^ (h1 >>> 13), 3266489909)
  return (4294967296 * (2097151 & h2) + (h1 >>> 0)).toString()
}

/**
 * Sets the value of a native DOM input element like user input would do.
 *
 * @param input Reference to DOM input element
 * @param value The value to set
 */
export const setDomInputValue = (
  input: HTMLInputElement | null,
  value: string,
) => {
  if (input) {
    // Setting input.value directly will not trigger a React event.
    // This works as expected. See https://stackoverflow.com/a/46012210/2270233
    const nativeValueProperty = Object.getOwnPropertyDescriptor(
      window.HTMLInputElement.prototype,
      "value",
    )
    nativeValueProperty?.set?.call(input, value)
    const event = new Event("input", { bubbles: true })
    input.dispatchEvent(event)
  }
}

/**
 * Clears a native DOM input element like user input would do.
 *
 * @param input Reference to DOM input element
 */
export const clearDomInput = (input: HTMLInputElement | null) => {
  setDomInputValue(input, "")
}
